import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal';
import { usuarioExtensaoService } from '../../../services/usuarioExtensaoService';
import { usuarioService } from '../../../services/usuarioService';
import { extensaoService } from '../../../services/extensaoService';
import { useAuth } from '../../../contexts/AuthContext';

const initialState = {
  usuario_id: '',
  extensoes_ids: [],
  data_inicio: '',
  data_fim: '',
  observacoes: '',
  ativo: true
};

const UsuarioExtensaoBulkCreate = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(initialState);
  const [usuarios, setUsuarios] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [extensaoFilter, setExtensaoFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadUsuarios();
    loadExtensoes();
  }, []);

  const loadUsuarios = async () => {
    try {
      console.log('Carregando usuários...');
      const response = await usuarioService.list({ limit: 1000 });
      console.log('Usuários carregados:', response.data);
      setUsuarios(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    }
  };

  const loadExtensoes = async () => {
    try {
      console.log('Carregando extensões...');
      // includeAll=true para garantir que Super Admin veja todas as extensões
      const response = await extensaoService.list({ limit: 1000, includeAll: true });
      console.log('Extensões carregadas:', response.data);
      setExtensoes(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar extensões:', err);
    }
  };

  const validate = () => {
    if (!form.usuario_id) return 'Selecione um usuário.';
    if (form.extensoes_ids.length === 0) return 'Selecione pelo menos uma extensão.';
    
    if (form.data_inicio && form.data_fim) {
      const dataInicio = new Date(form.data_inicio);
      const dataFim = new Date(form.data_fim);
      if (dataFim <= dataInicio) {
        return 'A data de fim deve ser posterior à data de início.';
      }
    }
    
    return '';
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'checkbox' ? checked : value 
    });
    setError('');
  };

  const handleExtensaoToggle = (extensaoId) => {
    if (form.extensoes_ids.includes(extensaoId)) {
      setForm({
        ...form,
        extensoes_ids: form.extensoes_ids.filter(id => id !== extensaoId)
      });
    } else {
      setForm({
        ...form,
        extensoes_ids: [...form.extensoes_ids, extensaoId]
      });
    }
  };

  const handleSelectAllExtensoes = () => {
    const filteredExtensoes = extensoes.filter(extensao => 
      extensao.nome.toLowerCase().includes(extensaoFilter.toLowerCase())
    );
    setForm({
      ...form,
      extensoes_ids: filteredExtensoes.map(ext => ext.idExtensao)
    });
  };

  const handleDeselectAllExtensoes = () => {
    setForm({
      ...form,
      extensoes_ids: []
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      setShowConfirm(false);
      setLoading(true);
      setError('');
      
      await usuarioExtensaoService.adicionarAcessosLote(
        form.usuario_id,
        form.extensoes_ids,
        user?.id || 1,
        form.data_inicio || null,
        form.data_fim || null,
        form.observacoes.trim() || null
      );
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/seguranca/usuarios/extensoes');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao criar acessos em massa');
    } finally {
      setLoading(false);
    }
  };

  const filteredExtensoes = extensoes.filter(extensao => 
    extensao.nome.toLowerCase().includes(extensaoFilter.toLowerCase())
  );

  return (
    <div className="w-full bg-white rounded shadow p-4 mt-4">
      <h2 className="text-xl font-bold mb-3">Criar Acessos em Massa</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Usuário *</label>
            <select
              name="usuario_id"
              value={form.usuario_id}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="">Selecione um usuário</option>
              {usuarios.map(usuario => (
                <option key={usuario.id} value={usuario.id}>
                  {usuario.nome} ({usuario.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Extensões * ({form.extensoes_ids.length} selecionadas)</label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleSelectAllExtensoes}
                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-300 rounded"
                >
                  Selecionar Todas Filtradas
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAllExtensoes}
                  className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-300 rounded"
                >
                  Deselecionar Todas
                </button>
              </div>
            </div>
          
          <input
            type="text"
            placeholder="Filtrar extensões por nome..."
            value={extensaoFilter}
            onChange={(e) => setExtensaoFilter(e.target.value)}
            className="w-full mb-2 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-400"
          />
          
          <div className="border rounded max-h-20 overflow-y-auto p-1 bg-gray-50">
            {filteredExtensoes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhuma extensão encontrada</p>
            ) : (
              filteredExtensoes.map(extensao => (
                <label 
                  key={extensao.idExtensao} 
                  className="flex items-center py-0.5 px-1 hover:bg-white rounded cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="mr-2">
                    <FontAwesomeIcon 
                      icon={form.extensoes_ids.includes(extensao.idExtensao) ? faCheckSquare : faSquare}
                      className={`text-sm ${form.extensoes_ids.includes(extensao.idExtensao) ? 'text-green-600' : 'text-gray-400'}`}
                    />
                  </div>
                  <input
                    type="checkbox"
                    checked={form.extensoes_ids.includes(extensao.idExtensao)}
                    onChange={() => handleExtensaoToggle(extensao.idExtensao)}
                    className="sr-only"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{extensao.nome}</div>
                    <div className="text-xs text-gray-500 truncate">{extensao.cidade}/{extensao.uf}</div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <label className="block text-sm font-medium mb-1">Data de Início</label>
            <input
              type="date"
              name="data_inicio"
              value={form.data_inicio}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <p className="text-xs text-gray-500 mt-1">Aplicada a todos os acessos</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Data de Fim</label>
            <input
              type="date"
              name="data_fim"
              value={form.data_fim}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <p className="text-xs text-gray-500 mt-1">Aplicada a todos os acessos</p>
          </div>
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">Observações</label>
          <textarea
            name="observacoes"
            value={form.observacoes}
            onChange={handleChange}
            rows={1}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Observações que serão aplicadas a todos os acessos criados (opcional)"
          />
        </div>

        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">Status</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={true}
                checked={form.ativo === true}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: true }))}
                className="mr-2"
              />
              Ativo
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={false}
                checked={form.ativo === false}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: false }))}
                className="mr-2"
              />
              Inativo
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">Status aplicado a todos os acessos</p>
        </div>

        {form.extensoes_ids.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2">
            <p className="text-blue-800 text-xs">
              <strong>Resumo:</strong> Serão criados {form.extensoes_ids.length} acessos para o usuário selecionado.
            </p>
          </div>
        )}

        {error && <div className="text-red-600 text-xs bg-red-50 border border-red-200 rounded p-2 mb-2">{error}</div>}
        {loading && <div className="text-blue-600 text-xs bg-blue-50 border border-blue-200 rounded p-2 mb-2">Criando acessos...</div>}
        {success && <div className="text-green-600 text-xs bg-green-50 border border-green-200 rounded p-2 mb-2">Acessos criados com sucesso!</div>}
        
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => navigate('/seguranca/usuarios/extensoes')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button 
            type="submit" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading || form.extensoes_ids.length === 0}
           aria-label="Criar Acessos">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Criar Acessos</span>
          </button>
        </div>
      </form>
      
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Criação em Massa"
        message={`Deseja realmente criar ${form.extensoes_ids.length} acessos para o usuário selecionado?`}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        confirmText="Criar Acessos"
        cancelText="Cancelar"
      />
      </div>
    
  );
};

export default UsuarioExtensaoBulkCreate;
